import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@chapche/ui';
import { GENERATION_TRACES_URL, getFetchRequest } from '../../store/requests';
import { errorCallback, makeUrl } from '../../util';
import Title from '../Title';
import Loading from '../Loading';

const OUTCOMES = ['', 'success', 'skip'];

const JsonBlock = ({ value }) => (
    <pre className="m-0 overflow-x-auto rounded-input bg-light-beige p-16 font-montserrat text-s text-ink">
        {JSON.stringify(value, null, 2)}
    </pre>
);

const GenerationTracesPage = () => {
    const { i18n } = useTranslation();
    const t = (key) => i18n.t(key);
    const navigate = useNavigate();
    const { generationId } = useParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [filters, setFilters] = useState({
        userId: '',
        outcome: '',
        skipReason: '',
        from: '',
        to: '',
    });

    const outcomeOptions = useMemo(
        () => OUTCOMES.map((value) => ({
            value,
            label: value || t('app.page.generationTrace.anyOutcome'),
        })),
        [i18n.language],
    );

    const loadList = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filters.userId) params.set('userId', filters.userId.trim());
        if (filters.outcome) params.set('outcome', filters.outcome);
        if (filters.skipReason) params.set('skipReason', filters.skipReason.trim());
        if (filters.from) params.set('from', new Date(filters.from).toISOString());
        if (filters.to) params.set('to', new Date(filters.to).toISOString());
        params.set('limit', '50');
        params.set('offset', '0');
        getFetchRequest(
            makeUrl([GENERATION_TRACES_URL, `?${params.toString()}`]),
            (response) => {
                setItems(response?.items || []);
                setTotal(response?.total || 0);
                setLoading(false);
            },
            (err) => {
                setLoading(false);
                errorCallback(err);
            },
        );
    }, [filters]);

    useEffect(() => {
        if (!generationId) {
            loadList();
            setDetail(null);
            return;
        }
        setLoading(true);
        getFetchRequest(
            makeUrl([GENERATION_TRACES_URL, `${generationId}/`]),
            (response) => {
                setDetail(response);
                setLoading(false);
            },
            (err) => {
                setLoading(false);
                errorCallback(err);
            },
        );
    }, [generationId, loadList]);

    const onFilterChange = (key) => (event) => {
        setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    };

    if (generationId) {
        const fields = detail
            ? [
                ['ID', detail.id],
                [t('app.page.generationTrace.userId'), detail.userId],
                [t('app.page.generationTrace.outcome'), detail.outcome],
                [t('app.page.generationTrace.skipReason'), detail.skipReason || '—'],
                [t('app.page.generationTrace.mealId'), detail.mealId || '—'],
                [t('app.page.generationTrace.recipeId'), detail.recipeId || '—'],
                [t('app.page.generationTrace.mealType'), detail.mealType || '—'],
                [t('app.page.generationTrace.createdAt'), detail.createdAt],
            ]
            : [];

        return (
            <section className="main__content workspace flex-col">
                <div className="product__header w-full">
                    <Title titleValue={t('app.page.generationTrace.title')} />
                    <Button variant="outline" onClick={() => navigate('/generation-trace')}>
                        {t('app.page.generationTrace.back')}
                    </Button>
                </div>
                {loading ? <Loading /> : null}
                {!loading && detail ? (
                    <article className="w-full max-w-[960px] rounded-input border border-solid border-light-grey bg-white p-24">
                        <dl className="m-0 grid grid-cols-[minmax(120px,180px)_1fr] gap-x-16 gap-y-12 text-m">
                            {fields.map(([label, value]) => (
                                <React.Fragment key={label}>
                                    <dt className="text-ink-muted">{label}</dt>
                                    <dd className="m-0 break-all text-ink">{value}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                        <h3 className="block__title-h3 mt-24 mb-12">
                            {t('app.page.generationTrace.summary')}
                        </h3>
                        <JsonBlock value={detail.summary} />
                        <h3 className="block__title-h3 mt-24 mb-12">
                            {t('app.page.generationTrace.timeline')}
                        </h3>
                        <ol className="m-0 flex list-none flex-col gap-12 p-0">
                            {(detail.events || []).map((event, index) => (
                                <li
                                    key={`${event.type}-${index}`}
                                    className="rounded-input border border-solid border-border p-16"
                                >
                                    <strong className="mb-8 block text-m text-green-accent">
                                        {event.type}
                                    </strong>
                                    <JsonBlock value={event} />
                                </li>
                            ))}
                        </ol>
                    </article>
                ) : null}
            </section>
        );
    }

    return (
        <section className="main__content workspace flex-col">
            <div className="product__header w-full">
                <Title titleValue={t('app.page.generationTrace.title')} />
            </div>
            <form
                className="mb-16 flex w-full flex-wrap items-end gap-12"
                onSubmit={(event) => {
                    event.preventDefault();
                    loadList();
                }}
            >
                <div className="w-[240px] max-w-full shrink-0">
                    <Input
                        fullWidth
                        value={filters.userId}
                        onChange={onFilterChange('userId')}
                        placeholder={t('app.page.generationTrace.userId')}
                    />
                </div>
                <div className="w-[220px] max-w-full shrink-0">
                    <Select
                        fullWidth
                        className="mt-0"
                        options={outcomeOptions}
                        value={filters.outcome}
                        onChange={(value) => setFilters((prev) => ({ ...prev, outcome: String(value) }))}
                        placeholder={t('app.page.generationTrace.anyOutcome')}
                    />
                </div>
                <div className="w-[240px] max-w-full shrink-0">
                    <Input
                        fullWidth
                        value={filters.skipReason}
                        onChange={onFilterChange('skipReason')}
                        placeholder={t('app.page.generationTrace.skipReason')}
                    />
                </div>
                <div className="w-[220px] max-w-full shrink-0">
                    <Input
                        fullWidth
                        type="datetime-local"
                        value={filters.from}
                        onChange={onFilterChange('from')}
                        placeholder={t('app.page.generationTrace.from')}
                        aria-label={t('app.page.generationTrace.from')}
                    />
                </div>
                <div className="w-[220px] max-w-full shrink-0">
                    <Input
                        fullWidth
                        type="datetime-local"
                        value={filters.to}
                        onChange={onFilterChange('to')}
                        placeholder={t('app.page.generationTrace.to')}
                        aria-label={t('app.page.generationTrace.to')}
                    />
                </div>
                <Button type="submit">{t('app.page.generationTrace.applyFilters')}</Button>
            </form>
            <p className="block__content mb-12 w-full text-ink-muted">
                {t('app.page.generationTrace.total')}: {total}
            </p>
            {loading ? <Loading /> : null}
            {!loading && items.length === 0 ? (
                <p className="block__content w-full">{t('app.page.generationTrace.empty')}</p>
            ) : null}
            {!loading && items.length > 0 ? (
                <div className="w-full overflow-x-auto rounded-input border border-solid border-light-grey bg-white">
                    <table className="w-full border-collapse text-left text-m text-ink">
                        <thead>
                            <tr className="bg-light-beige">
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.createdAt')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.userId')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.outcome')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.skipReason')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.mealType')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.recipeId')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.targetKcal')}
                                </th>
                                <th className="whitespace-nowrap px-16 py-12 font-montserrat-semibold text-dark-green">
                                    {t('app.page.generationTrace.actualKcal')}
                                </th>
                                <th className="px-16 py-12" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr
                                    key={item.id}
                                    className="border-b border-solid border-light-grey last:border-b-0 hover:bg-select-hover"
                                >
                                    <td className="whitespace-nowrap px-16 py-12">{item.createdAt}</td>
                                    <td className="px-16 py-12">{item.userId}</td>
                                    <td className="px-16 py-12">{item.outcome}</td>
                                    <td className="px-16 py-12">{item.skipReason || '—'}</td>
                                    <td className="px-16 py-12">{item.mealType || item.summary?.mealType || '—'}</td>
                                    <td className="px-16 py-12">{item.recipeId || item.summary?.recipeId || '—'}</td>
                                    <td className="px-16 py-12">{item.summary?.targetKcal || '—'}</td>
                                    <td className="px-16 py-12">{item.summary?.actualKcal || '—'}</td>
                                    <td className="px-16 py-12">
                                        <Button
                                            size="small"
                                            variant="outline"
                                            onClick={() => navigate(`/generation-trace/${item.id}`)}
                                        >
                                            {t('app.page.generationTrace.open')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
};

export default GenerationTracesPage;
